<template>
  <div
    ref="parcelTable"
    :style="tableHeight"
    class="spl-parcel-table">
    <table
      border="1"
      cellpadding="0"
      cellspacing="0">
      <thead>
        <tr class="spl-parcel-table__head">
          <th class="spl-parcel-table__head--checkbox">
            <form-checkbox
              :checked="allParcelsSelected"
              @checkboxChanged="checkAllParcels" />
          </th>
          <th
            v-for="(header, index) in visibleFields"
            :key="'dashboardPrecariageHeader' + index"
            :class="{'spl-parcel-table__head--sticky': (header.name === constants.PARCEL_FIELD_KEYS.TRACKER_LINK || header.name === constants.REFERENCE_TYPE_NAME.COUNTER_NUMBER) && index === 0 && parcelListData.length > 0, 'spl-parcel-table__head--sticky-second': header.name === constants.PARCEL_FIELD_KEYS.TRACKER_LINK && index === 1 && parcelListData.length > 0}"
            @click="toggleSort(header.name)">
            <span
              v-if="findSort(sortedFields, header.name)"
              :class="findSort(sortedFields, header.name).direction"
              class="order-arrow" />
            {{ header.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(parcel, parcelIndex) in parcelListData"
          :key="'dashboardPrecariageField' + parcelIndex"
          :class="{'spl-parcel-table__row--selected': parcel.isSelected,
                   'spl-parcel-table__row--deleted': parcel.Cancelled,
                   'spl-parcel-table__row--return': !parcel.Is_Forward}"
          class="spl-parcel-table__row">
          <td class="spl-parcel-table__field--checkbox">
            <form-checkbox
              :checked="parcel.isSelected"
              @checkboxChanged="checkParcel(parcel)" />
          </td>
          <td
            v-for="(field, fieldIndex) in visibleFields"
            :key="'dashboardPrecariageField' + parcelIndex + '.' + fieldIndex"
            :class="{'spl-parcel-table__field--number': isNumber(parcel[field.name]),
                     'spl-parcel-table__field--empty': isEmpty(parcel[field.name]),
                     'spl-parcel-table__field--sticky': field.fixed,
                     'spl-parcel-table__field--sticky-second': field.fixedSecond,
                     'spl-parcel-table__field--long': field.long,
                     'spl-parcel-table__field--bold': field.bold,
                     'spl-parcel-table__field--error': isError(parcel, field),
                     'spl-parcel-table__field--warning': isWarning(parcel, field),
                     'spl-parcel-table__field--success': isSuccess(parcel, field)}"
            :name="field.name"
            @click="parcelClicked(parcel, field)">
            <external-tracker
              v-if="field.name === 'tracker_link'"
              :short="true"
              :provider-name="parcel.provider_name"
              :tracker-link="parcel.tracker_link" />
            <span v-else>
              {{ formatField(parcel[field.name], field.name, field.type) }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import constants from '@/constants'
import Helpers from '@/helpers.vue'
import { find, filter, findIndex, orderBy, isNumber, round } from 'lodash'

export default Vue.extend({
  mixins: [Helpers],
  props: ['listHeaderColumnGroups', 'parcelList'],
  data () {
    return {
      visibleFields: [],
      sortedFields: [],
      parcelListData: [],
      numSelectedParcels: 0,
      containerMonitor: null,
      constants
    }
  },
  computed: {
    allParcelsSelected () {
      for (let i = 0; i < this.parcelListData.length; i++) {
        if (!this.parcelListData[i].isSelected) {
          return false
        }
      }
      return true
    },
    tableHeight () {
      if (this.$mq === 'sm') {
        return ''
      }
      let heightModifier = 212
      if (this.showActionBar()) {
        heightModifier += constants.ACTION_BAR_HEIGHT
      }
      if (this.showSelectedParcelSummary()) {
        heightModifier += constants.PARCEL_LIST_SUMMARY_HEIGHT
      }
      return 'height: calc(100vh - ' + heightModifier + 'px);'
    }
  },
  watch: {
    listHeaderColumnGroups: {
      handler: function (newVal) {
        // Changes the visible fields when to user selects the fields
        this.buildVisibleFields()
      },
      deep: true
    },
    parcelList: {
      handler: function (newVal) {
        this.parcelListData = this.parcelList
        this.sortData()
        this.checkAllParcels(false)
      },
      deep: true
    }
  },
  mounted () {
    this.buildVisibleFields()
  },
  methods: {
    copyHTML () {
      window.addEventListener('copy', (event:ClipboardEvent) => {
        event.preventDefault()
        event.clipboardData.setData('text/html', this.$refs.parcelTable.innerHTML)
      })
      document.execCommand('copy')
    },
    clearSelection () {
      this.numSelectedParcels = 0
    },
    // shows only selected fields from Field selector
    buildVisibleFields () {
      if (this.listHeaderColumnGroups) {
        // Copies the defaultfields
        const tempVisibleFields = []
        // select fields from groups and put them in a flat hierarchy
        for (let i = 0; i < this.listHeaderColumnGroups.length; i++) {
          const group = this.listHeaderColumnGroups[i]
          for (let j = 0; j < group.columns.length; j++) {
            const column = group.columns[j]
            if (column.selected) {
              tempVisibleFields.push(column)
            }
          }
        }
        this.visibleFields = tempVisibleFields
        this.sortData()
      }
    },
    parcelClicked (parcel, field) {
      if (window.getSelection().toString().length > 0) {
        return
      }
      const parcelID = parcel.Parcel_ID
      this.$emit('parcelDetailOpened', parcelID)
    },
    checkAllParcels (checked) {
      for (let i = 0; i < this.parcelListData.length; i++) {
        const parcel = this.parcelListData[i]
        parcel.isSelected = checked
      }
      this.shoutSelectionChange()
    },
    checkParcel (parcel) {
      parcel.isSelected = !parcel.isSelected
      this.shoutSelectionChange()
    },
    shoutSelectionChange () {
      this.$forceUpdate()
      const selectedParcels = filter(this.parcelListData, 'isSelected')
      this.numSelectedParcels = selectedParcels.length
      this.$emit('selectionChanged', selectedParcels)
    },
    toggleSort (field) {
      // Finds the sorted field in the array
      var sortedField:any = find(this.sortedFields, { field: field })
      if (sortedField) {
        // Switches to desc when asc already selected
        if (sortedField.direction === 'asc') {
          sortedField.direction = 'desc'
        } else {
          // removes the sorting when desc was selected
          this.sortedFields.splice(findIndex(this.sortedFields, { field: field }))
        }
      } else {
        // adds the field when not yet present
        this.sortedFields.push({ direction: 'asc', field: field })
      }
      this.sortData()
    },
    // Sorting the data
    sortData () {
      // Sort fields and directions seperate for lodash orderBy method
      let sortFields = []
      let sortDirections = []
      // Create the sort fields
      for (let i = 0; i < this.sortedFields.length; i++) {
        const sortElement = this.sortedFields[i]
        sortFields.push(sortElement.field)
        sortDirections.push(sortElement.direction)
      }
      // Default sorting depending on grouping
      if (sortFields.length === 0) {
        sortFields = ['Delivery_Postal_Code', 'Delivery_City']
        sortDirections = ['asc', 'asc']
      }
      // Use the lodash orderBy method for sorting
      this.parcelListData = orderBy(this.parcelListData, sortFields, sortDirections)
    },
    getReference (parcel, reference) {
      const foundReference:any = find(parcel.References, { Reference_Type: reference })
      if (foundReference) {
        return foundReference.Reference_Text
      } else {
        return ''
      }
    },
    // Special formatting for fields
    formatField (field, name, type) {
      // Weight & Picked up weight will be rounded to two decimal places then fixed in order to improve readability
      if (name === constants.PARCEL_FIELD_KEYS.WEIGHT) {
        return round(field, 2).toFixed(2)
      } else if (name === constants.PARCEL_FIELD_KEYS.VOLUME) {
        // Volume will be rounded to 3 decimal places
        return round(field, 3).toFixed(3)
      } else if (type === 'Date') {
        return this.formatDate(field, false, 'YYYY-MM-DD')
      } else if (type === 'Time') {
        return this.formatDate(field, true, 'YYYY-MM-DD HH:mm')
      }
      // empty fields will be removed
      if (field == null || field === 0) {
        return ''
      }
      return field
    },
    // including of helper since lodash not available in vue template
    isNumber (value) {
      return isNumber(value)
    },
    // find the right sort value for displaying arrows
    findSort (array, value) {
      return find(array, { field: value })
    },
    // helper for removing 0 elements
    isEmpty (value) {
      return value === 0
    },
    isError (parcel, field) {
      if (field.name === constants.PARCEL_FIELD_KEYS.CURRENT_PARCEL_STATUS || field.name === constants.PARCEL_FIELD_KEYS.PROBLEM_TYPE_DESCRIPTION) {
        if (parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_EXTERNAL_DELAY] ||
          parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_LATE] ||
          parcel[constants.PARCEL_FIELD_KEYS.PROBLEM_TYPE_DESCRIPTION] ||
          parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_HAS_REPORTED_EXCEPTION] ||
          parcel[constants.PARCEL_FIELD_KEYS.CANCELLED]) {
          return true
        }
      } else {
        return false
      }
    },
    isWarning (parcel, field) {
      if (field.name !== constants.PARCEL_FIELD_KEYS.CURRENT_PARCEL_STATUS) {
        return false
      }
      if (parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_NOT_BOOKED] ||
          parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_NOT_COLLECTED] ||
          parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_EARLY]) {
        return true
      } else {
        return false
      }
    },
    isSuccess (parcel, field) {
      if (field.name !== constants.PARCEL_FIELD_KEYS.CURRENT_PARCEL_STATUS) {
        return false
      }
      if (parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_DELIVERED] ||
          parcel[constants.PARCEL_FIELD_KEYS.PARCEL_STATUS_ON_TIME]) {
        return true
      } else {
        return false
      }
    },
    showActionBar () {
      return this.userHasActionBarPermissions() && this.numSelectedParcels > 0
    },
    showSelectedParcelSummary () {
      return this.numSelectedParcels > 1
    }
  }
})
</script>

<style lang="scss">
  @import '../../variables.scss';
  .spl-parcel-table {
    margin: 0 70px;
    overflow: auto;
    width: calc(100% - 138px);

    table {
      border-spacing: 0;
      table-layout: fixed;
      border: 0;
    }
    &__head {
      th {
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 3px 5px 0 rgba(0, 0, 0, 0.1);
        border: 0;
      }
      &--sticky {
        position: sticky;
        left: 41px;
        width: 150px;
        z-index: 3 !important;
      }
      &--sticky-second {
        position: sticky;
        left: 211px;
        z-index: 3 !important;
      }
      &--checkbox {
        .spl-checkbox {
          margin: 10px 2px 0 6px;
        }
        position: sticky;
        left: 0px;
        z-index: 3 !important;
      }
    }
    &__row {
      &--selected {
        background-color: $tableRowBackgroundColorSelected !important;
      }
      &--deleted {
        opacity: 0.3;
        background-color: lightgrey !important;
      }
      &--return [name='Counter Number'] {
        background-color: #fbd5d5;
      }
    }
    &__field {
      &--number {
        text-align: right;
        width: 70px;
      }
      &--sticky {
        position: sticky;
        min-width: 170px;
        left: 41px;
        box-shadow: 3px 0px 3px 0px rgba(0, 0, 0, 0.2);
        background-color: $tableHeaderBackgroundColor;
      }
      &--sticky-second {
        position: sticky;
        left: 211px;
        box-shadow: 3px 0px 3px 0px rgba(0, 0, 0, 0.2);
        background-color: $tableHeaderBackgroundColor;
      }
      &--checkbox {
        .spl-checkbox {
          margin: 0 7px 0 6px;
        }
        position: sticky;
        background-color: $tableHeaderBackgroundColor;
        left: 0;
      }
      &--long {
        max-width: 400px !important;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      &--success {
        border-bottom: 2px solid $green !important;
        padding-bottom: 3px !important;
        font-weight: 600;
        color: $green;
      }
      &--warning {
        border-bottom: 2px solid $darkYellow !important;
        padding-bottom: 3px !important;
        font-weight: 600;
        color: $darkYellow;
      }
      &--error {
        border-bottom: 2px solid $red !important;
        padding-bottom: 3px !important;
        font-weight: 600;
        color: $red;
      }
      &--bold {
        font-weight: 600;
      }
      &--empty:hover {
        background-color: $tableRowBackgroundColorHover !important;
      }
    }
    thead {
      th {
        position: sticky;
        top: 0;
        text-align: left;
        font-family: $mono;
        font-size: 9px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1px;
        line-height: 12px;
        background-color: $tableHeaderBackgroundColor;
        padding: 0px 5px  0px 5px;
        height: 40px;
        z-index: 2;
        border-bottom: 1px solid $tableHeaderBorderColor;
      }

      th:hover {
        background-color: $tableColumnBackgroundColorHover;
        cursor: pointer;
      }
    }
    tbody {

      tr {
        background-color: $tableRowBackgroundColor;
      }

      tr:hover {
        background-color: $tableRowBackgroundColorHover;
      }

      td {
        border-style: solid;
        border-width: 0 1px 1px 0;
        border-color: $tableRowBorderLowColor;
        border-bottom-color: $tableRowBorderColor;
        font-size: 12px;
        padding: 5px;
        padding-top: 8px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 150px;
      }

      td:hover {
        background-color: $tableColumnBackgroundColorHover;
        cursor: pointer;
      }

      td:first-child {
        border-left-width: 1px;
      }
    }

  }
  @include mobile {
    .spl-parcel-table {
      margin: 0 10px 300px 10px;
      overflow: auto;
      width: 100%;
      height: auto;
    }
  }
</style>
